// src/resources/reports/ReportPushNotifications/TimelineChart.tsx
//
// "Linha do tempo" (§4 bloco 3) — barras diárias + linha da taxa de leitura.
// O tooltip lista os títulos que saíram naquele dia: é o "quando foi cada
// envio" que o dono do posto pediu, em vez do número solto de disparos.
import React, { useMemo, useState } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import {
  Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip as ReTooltip, XAxis, YAxis,
} from 'recharts';
import { SeriesPoint, fmtAxisPct, fmtInt, fmtPct, niceRateDomainMax } from './types';

const BLUE = '#3b82f6';
const INK = '#0f172a';

// Métrica selecionável pra barra diária — enviadas é o padrão do §4 bloco 3,
// mas comparar com entregues/lidas/clicadas no mesmo eixo ajuda a achar o dia
// que o funil despencou.
type BarMetric = 'sent' | 'delivered' | 'read' | 'clicked';

const BAR_METRICS: { key: BarMetric; label: string }[] = [
  { key: 'sent', label: 'Enviadas' },
  { key: 'delivered', label: 'Entregues' },
  { key: 'read', label: 'Lidas' },
  { key: 'clicked', label: 'Clicadas' },
];

/** 'YYYY-MM-DD' → 'dd/mm'. Sem Date no meio: string de data pura vira UTC e volta um dia. */
const shortDate = (iso: string): string => {
  const [, month, day] = iso.split('-');
  return month && day ? `${day}/${month}` : iso;
};

const TimelineTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const point: SeriesPoint | undefined = payload[0]?.payload?.raw;
  if (!point) return null;

  return (
    <Box sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 1.5, p: 1.25, boxShadow: '0 4px 16px rgba(15,23,42,0.12)', maxWidth: 260 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: INK }}>{shortDate(String(label))}</Typography>
      <Typography sx={{ fontSize: 11.5, color: '#64748b', mt: 0.25 }}>
        {fmtInt(point.sent)} enviadas · {fmtPct(point.readRate)} de leitura
      </Typography>
      {/* `titles` é montado no front a partir das mensagens do dia; guarda contra
          resposta sem o campo (rollout parcial do backend) pra não quebrar o tooltip. */}
      {(point.titles ?? []).length > 0 && (
        <Box sx={{ mt: 0.75, borderTop: '1px solid #f1f5f9', pt: 0.75 }}>
          <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Envios do dia
          </Typography>
          {(point.titles ?? []).map((title, i) => (
            <Typography key={i} sx={{ fontSize: 11.5, color: INK, mt: 0.25 }}>• {title}</Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

const TimelineChart = ({ series }: { series: SeriesPoint[] }) => {
  const [metric, setMetric] = useState<BarMetric>('sent');

  const chartData = useMemo(() => series.map(point => ({
    date: point.date,
    bar: point[metric],
    readRatePct: point.readRate !== null ? point.readRate * 100 : null,
    raw: point,
  })), [series, metric]);

  const rateAxisMax = useMemo(() => niceRateDomainMax(chartData.map(d => d.readRatePct)), [chartData]);

  return (
    <Box sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 2, p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Linha do tempo</Typography>
          <Typography sx={{ fontSize: 12, color: '#64748b' }}>Quando cada envio saiu e como a leitura reagiu</Typography>
        </Box>
        {/* Seletor segmentado (pílula) — mesmo componente visual de
            ReportAppPenetration/TrendChart.tsx. O rádio já foi reprovado pelo
            usuário por destoar do resto da tela (cards, chips, barras). */}
        <Box sx={{ display: 'inline-flex', gap: 0.4, p: 0.4, bgcolor: '#f1f5f9', borderRadius: 999 }}>
          {BAR_METRICS.map(m => {
            const active = m.key === metric;
            return (
              <ButtonBase
                key={m.key}
                onClick={() => setMetric(m.key)}
                sx={{
                  px: 1.75, py: 0.7, borderRadius: 999, fontSize: 12.5, fontWeight: 700,
                  color: active ? '#fff' : '#64748b',
                  background: active ? `linear-gradient(135deg, ${BLUE}, #06b6d4)` : 'transparent',
                  boxShadow: active ? '0 2px 8px rgba(59,130,246,0.35)' : 'none',
                  transition: 'background 0.18s ease, color 0.18s ease, box-shadow 0.18s ease',
                  '&:hover': active ? {} : { color: '#0f172a', bgcolor: 'rgba(148,163,184,0.18)' },
                }}
              >
                {m.label}
              </ButtonBase>
            );
          })}
        </Box>
      </Box>

      {!chartData.length ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">Sem envio no período.</Typography>
        </Box>
      ) : (
        <Box sx={{ height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.22)" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickLine={false} minTickGap={16} tickFormatter={shortDate} />
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={48} tickFormatter={(v: number) => fmtInt(v)} />
              {/* Escala da taxa sai dos dados (niceRateDomainMax) — 0–100% fixo achatava a
                  linha de leitura, que anda abaixo de 2%. */}
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={52}
                domain={[0, rateAxisMax]} tickFormatter={(v: number) => fmtAxisPct(v, rateAxisMax)} />
              <ReTooltip content={<TimelineTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} iconSize={8} />
              <Bar yAxisId="left" dataKey="bar" name={BAR_METRICS.find(m => m.key === metric)?.label} fill={BLUE} radius={[2, 2, 0, 0]} maxBarSize={28} />
              <Line yAxisId="right" dataKey="readRatePct" name="Taxa de leitura" stroke={INK} strokeWidth={2} dot={false} connectNulls />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Box>
  );
};

export default TimelineChart;
